import React, { useState, useEffect, useRef } from 'react';
import { apiErrorText } from '../lib/apiError';
import useSWR from 'swr';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ActionButton from './ui/action-button';
import { LayoutActionButton } from './Buttons';
import ServiceToggle from './ServiceToggle';
import { Label } from './ui/label';
import { Camera, Search, Check, X, QrCode, Loader2, Users, Calendar, Clock, Clipboard, Copy, Mail, CheckCircle, AlertCircle, ChevronDown, ExternalLink, DollarSign, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import apiV2 from '../utils/apiV2';
import api from '../utils/api'; // Keep legacy for public endpoints
import TabPageLayout, { PageLoader, EmptyState } from './TabPageLayout';
import AutoSaveIndicator from './AutoSaveIndicator';
import FormSection from './FormSection';
import jsQR from 'jsqr';
import { QRCodeCanvas } from 'qrcode.react';
import getTicketLink from '../utils/ticketLink';

const CheckInTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkInEnabled, setCheckInEnabled] = useState(false);
  const [checkInPin, setCheckInPin] = useState('000000');
  
  // Track original values to detect unsaved changes
  const [originalEnabled, setOriginalEnabled] = useState(false);
  const [originalPin, setOriginalPin] = useState('000000');
  
  // Computed: do we have unsaved changes?
  const hasUnsavedChanges = checkInEnabled !== originalEnabled || checkInPin !== originalPin;
  
  const [scanMode, setScanMode] = useState('manual');
  const [searchQuery, setSearchQuery] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  // Recent check-ins filter state
  const [checkInQ, setCheckInQ] = useState('');
  const [checkInStart, setCheckInStart] = useState('');
  const [checkInEnd, setCheckInEnd] = useState('');
  const [checkInDateField, setCheckInDateField] = useState('checkin'); // 'checkin' | 'booking'
  const [exportingCheckIns, setExportingCheckIns] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResultsModal, setShowSearchResultsModal] = useState(false);
  const [searchResultsPage, setSearchResultsPage] = useState(1);
  const searchResultsPerPage = 5;
  
  // QR Code modal for scanner link
  const [showScannerQR, setShowScannerQR] = useState(false);
  const [scannerQRUrl, setScannerQRUrl] = useState('');
  
  // QR Scanner dropdown
  const [showScannerDropdown, setShowScannerDropdown] = useState(false);
  const scannerDropdownRef = useRef(null);
  
  // SWR fetcher function using apiV2
  const fetcher = (url) => apiV2.get(url).then(res => {
    // Handle both array and object responses
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.bookings)) return data.bookings;
    if (data && Array.isArray(data.items)) return data.items;
    return [];
  });
  
  // Use SWR for recent check-ins with auto-refresh
  const orgId = typeof window !== 'undefined' ? localStorage.getItem('organizationId') : null;
  const { data: recentCheckIns = [], error: checkInsError, isLoading: loadingCheckIns, mutate: refreshCheckIns } = useSWR(
    checkInEnabled && orgId ? `/organizations/${orgId}/bookings?checkedIn=true` : null,
    fetcher,
    {
      refreshInterval: 30000,
      revalidateOnFocus: true,
      dedupingInterval: 5000
    }
  );
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailBooking, setEmailBooking] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Sorting and filtering
  const [sortBy, setSortBy] = useState('recent');
  const [filterDate, setFilterDate] = useState('all');
  const [checkInPage, setCheckInPage] = useState(1);
  const checkInsPerPage = 10;
  
  // Selected check-in for details
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [showCheckInDetails, setShowCheckInDetails] = useState(false);
  
  // Check-in confirmation
  const [showCheckInConfirm, setShowCheckInConfirm] = useState(false);
  const [bookingToCheckIn, setBookingToCheckIn] = useState(null);
  
  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanIntervalRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    return () => {
      stopCamera();
    };
  }, []);
  
  // Close scanner dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scannerDropdownRef.current && !scannerDropdownRef.current.contains(event.target)) {
        setShowScannerDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const fetchSettings = async () => {
    try {
      const orgResponse = await apiV2.get('/auth/organizations/me');
      const orgData = orgResponse.data;
      
      const enabled = orgData.checkInEnabled || false;
      const pin = orgData.checkInPin || '000000';
      
      setCheckInEnabled(enabled);
      setCheckInPin(pin);
      
      // Store original values to track changes
      setOriginalEnabled(enabled);
      setOriginalPin(pin);
    } catch (error) {
      console.error('Error fetching check-in settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async (overrides = {}) => {
    // React state updates are async — callers changing a value in the same tick
    // (e.g. the enable toggle) must pass it explicitly or we save stale state.
    const enabled = overrides.enabled !== undefined ? overrides.enabled : checkInEnabled;
    const pin = overrides.pin !== undefined ? overrides.pin : checkInPin;
    if (enabled && (!/^\d{6}$/.test(pin))) {
      toast.error('PIN must be exactly 6 digits');
      return;
    }

    setSaving(true);
    try {
      await apiV2.patch('/auth/organizations/me', {
        checkInEnabled: enabled,
        checkInPin: pin
      });

      fetchSettings();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save settings';
      toast.error(`Save error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setCameraActive(true);
        setScanning(true);
        startScanning();
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Failed to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setScanning(false);
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
  };

  const startScanning = () => {
    scanIntervalRef.current = setInterval(() => {
      if (videoRef.current && canvasRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code) {
          handleQRCodeDetected(code.data);
        }
      }
    }, 300);
  };

  const handleQRCodeDetected = (qrData) => {
    try {
      const url = new URL(qrData);
      const pathParts = url.pathname.split('/');
      const confirmationCode = pathParts[pathParts.length - 1];
      
      if (confirmationCode && confirmationCode.startsWith('CS-')) {
        stopCamera();
        setSearchQuery(confirmationCode);
        toast.success(`QR Code scanned: ${confirmationCode}`);
        setTimeout(() => handleManualCheckIn(), 500);
      }
    } catch (error) {
      // Invalid QR code format
    }
  };

  const handleCheckInSuccess = () => {
    refreshCheckIns();
  };

  const handleManualCheckIn = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a booking ID, name, email, or phone');
      return;
    }

    setCheckingIn(true);
    try {
      const searchResponse = await apiV2.get(`/organizations/${orgId}/bookings`, {
        params: { query: searchQuery }
      });
      
      const bookings = searchResponse.data.bookings || [];
      
      if (bookings.length === 0) {
        toast.error('No booking found with that search');
        return;
      }
      
      setSearchResults(bookings);
      setSearchResultsPage(1);
      setShowSearchResultsModal(true);
      
      if (bookings.length === 1) {
        toast.success('Found 1 booking!');
      } else {
        toast.success(`Found ${bookings.length} bookings!`);
      }
    } catch (error) {
      toast.error(apiErrorText(error, 'Search failed'));
    } finally {
      setCheckingIn(false);
    }
  };

  const checkInBooking = async (bookingId) => {
    const booking = searchResults.find(b => b.id === bookingId);
    if (!booking) {
      toast.error('Booking not found');
      return;
    }
    
    try {
      const response = await apiV2.post(`/organizations/${orgId}/bookings/${booking.id}/check-in`);
      
      if (response.data.alreadyCheckedIn) {
        toast.info('Customer already checked in');
      } else {
        toast.success('✅ Customer checked in successfully!');
      }
      
      setSearchResults(prevResults => 
        prevResults.map(b => 
          b.id === booking.id 
            ? { ...b, checkInStatus: 'checked_in', checkInAt: response.data.checkInAt }
            : b
        )
      );
      
      handleCheckInSuccess();
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error(apiErrorText(error, 'Check-in failed'));
    }
  };
  
  const openEmailModal = (booking) => {
    setEmailBooking(booking);
    setEmailTo(booking.customerEmail || '');
    setShowEmailModal(true);
  };
  
  // Generate a magic link for a booking (single source of truth: utils/ticketLink)
  const generateMagicLink = (booking) => getTicketLink(booking.confirmationCode);
  
  const sendTicketEmail = async () => {
    if (!emailTo || !emailTo.trim()) {
      toast.error('Please enter customer email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      // Generate magic link for the email
      const ticketUrl = await generateMagicLink(emailBooking);
      const orgName = localStorage.getItem('organizationName') || 'BookingFlows';
      
      const subject = `Your Booking Ticket - ${orgName}`;
      const message = `Hi ${emailBooking.customerName || 'there'},\n\nHere's your secure booking ticket link:\n\n${ticketUrl}\n\nThis link is personalized for you and will give you direct access to your booking details.\n\nSee you soon!\n\n${orgName}`;
      
      await api.post('/emails/send-to-customer', {
        email: emailTo,
        subject: subject,
        message: message
      });
      
      toast.success('✅ Magic link email sent successfully!');
      setShowEmailModal(false);
      setEmailBooking(null);
      setEmailTo('');
    } catch (error) {
      console.error('Email error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send email';
      if (errorMsg.includes('rate limit') || errorMsg.includes('too many')) {
        toast.error('Rate limit reached: Maximum 10 emails per customer per day');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSendingEmail(false);
    }
  };
  
  const confirmCheckIn = async () => {
    try {
      const response = await apiV2.post(`/organizations/${orgId}/bookings/${bookingToCheckIn.id}/check-in`);
      
      if (response.data.alreadyCheckedIn) {
        toast.info('Customer already checked in');
      } else {
        toast.success('Customer checked in successfully!');
      }
      
      setSearchResults(prevResults => 
        prevResults.map(booking => 
          booking.id === bookingToCheckIn.id 
            ? { ...booking, checkInStatus: 'checked_in', checkInAt: response.data.checkInAt }
            : booking
        )
      );
      
      setShowCheckInConfirm(false);
      setBookingToCheckIn(null);
      handleCheckInSuccess();
    } catch (error) {
      toast.error(apiErrorText(error, 'Check-in failed'));
    }
  };

  const formatDateTime = (dateStr, timeStr) => {
    const date = new Date(dateStr + 'T' + timeStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Filter and sort check-ins (with defensive array check)
  const checkInsArray = Array.isArray(recentCheckIns) ? recentCheckIns : [];

  // Apply local search + date filters before rendering Recent Check-Ins
  const filteredCheckIns = React.useMemo(() => {
    let list = checkInsArray;
    const q = checkInQ.trim().toLowerCase();
    if (q) {
      list = list.filter((b) =>
        (b.customerName || '').toLowerCase().includes(q) ||
        (b.serviceName || '').toLowerCase().includes(q) ||
        (b.confirmationCode || '').toLowerCase().includes(q) ||
        (b.customerEmail || '').toLowerCase().includes(q) ||
        (b.customerPhone || '').toLowerCase().includes(q)
      );
    }
    const startTs = checkInStart ? new Date(`${checkInStart}T00:00:00Z`).getTime() : null;
    const endTs = checkInEnd ? new Date(`${checkInEnd}T23:59:59Z`).getTime() : null;
    if (startTs || endTs) {
      list = list.filter((b) => {
        const raw = checkInDateField === 'booking' ? b.bookingDate : b.checkInAt;
        if (!raw) return false;
        const ts = new Date(raw).getTime();
        if (Number.isNaN(ts)) return false;
        if (startTs && ts < startTs) return false;
        if (endTs && ts > endTs) return false;
        return true;
      });
    }
    return list;
  }, [checkInsArray, checkInQ, checkInStart, checkInEnd, checkInDateField]);

  const handleExportCheckIns = () => {
    try {
      setExportingCheckIns(true);
      const header = ['Customer', 'Email', 'Phone', 'Service', 'Confirmation Code', 'Booking Date', 'Checked In At', 'Method'];
      const rows = filteredCheckIns.map((b) => [
        b.customerName || '',
        b.customerEmail || '',
        b.customerPhone || '',
        b.serviceName || '',
        b.confirmationCode || '',
        b.bookingDate || '',
        b.checkInAt || '',
        b.checkInMethod || '',
      ]);
      const csv = [header, ...rows]
        .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `checkins_${checkInStart || 'all'}_to_${checkInEnd || 'all'}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} check-ins`);
    } catch (err) {
      console.error('Check-in export failed:', err);
      toast.error('Could not export check-ins');
    } finally {
      setExportingCheckIns(false);
    }
  };

  return (
    <TabPageLayout
      title="Check-In"
      subtitle="QR scanning, ticket lookup, and attendance tracking"
      loading={loading}
      actions={<AutoSaveIndicator
        saving={saving}
        savedFlash={savedFlash}
        error={hasUnsavedChanges ? 'Unsaved PIN — press Enter or click outside' : null}
      />}
    >
      <div className="space-y-6">
        {/* Enable Check-In */}
        <div className="card-soft p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="settings-section-title">Enable Check-In</h3>
              <p className="settings-section-subtitle">
                Show a QR code on each customer ticket so staff can scan and check them in.
              </p>
            </div>
            <ServiceToggle
              checked={checkInEnabled}
              onChange={(checked) => {
                setCheckInEnabled(checked);
                handleSave({ enabled: checked });
              }}
              data-testid="enable-checkin-toggle"
            />
          </div>
        </div>

        {/* Check In Customer */}
        {checkInEnabled && (
          <div className="card-soft p-5 sm:p-6">
            <div className="mb-5">
              <h3 className="settings-section-title">Check In Customer</h3>
              <p className="settings-section-subtitle">
                Search a booking or open the QR scanner.
              </p>
            </div>

            {/* Manual lookup at the top — primary action */}
            <div className="space-y-2 mb-6">
              <div className="flex gap-2">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search booking ID, name, email, or phone…"
                  className="flex-1 h-10 text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleManualCheckIn()}
                  data-testid="checkin-manual-search-input"
                />
                <Button
                  onClick={handleManualCheckIn}
                  disabled={checkingIn || !searchQuery.trim()}
                  className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="checkin-manual-search-btn"
                >
                  {checkingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" />Search</>}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Or send guests to the{' '}
                <a href="/self-check-in" className="text-blue-600 hover:text-blue-700 font-medium">self check-in page</a>.
              </p>
            </div>

            {/* Divider */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
              <div className="relative flex justify-start">
                <span className="bg-white pr-2 text-xs uppercase tracking-wide text-gray-400">Or scan a ticket</span>
              </div>
            </div>

            {/* Two scanner methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Option 1: QR Scanner Gun */}
              <div className={`settings-card p-5 flex flex-col ${hasUnsavedChanges ? 'opacity-60' : ''}`}>
                <div className="flex items-center gap-2 mb-1">
                  <QrCode className="w-4 h-4 text-gray-700" />
                  <span className="text-sm font-semibold text-gray-900">QR Scanner Gun</span>
                </div>
                <p className="text-xs text-gray-500 mb-4">Launch a barcode-style scanner. Session lasts 12 hours.</p>

                {hasUnsavedChanges && (
                  <p className="text-[11px] text-amber-600 mb-2">Save PIN first</p>
                )}

                <div className="mt-auto relative" ref={scannerDropdownRef}>
                  <Button
                    onClick={() => {
                      if (hasUnsavedChanges) {
                        toast.error('Save the PIN first');
                        return;
                      }
                      setShowScannerDropdown(!showScannerDropdown);
                    }}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white h-9 text-sm disabled:bg-gray-300"
                    disabled={hasUnsavedChanges}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Scanner
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showScannerDropdown ? 'rotate-180' : ''}`} />
                  </Button>

                  {showScannerDropdown && !hasUnsavedChanges && (
                    <div className="absolute z-20 mt-2 w-full bg-white rounded-lg overflow-hidden card-soft">
                      <button
                        onClick={async () => {
                          try {
                            const response = await apiV2.post('/check-in/create-scanner-session');
                            const { token } = response.data;
                            window.location.href = `${window.location.origin}/qr-code-scanner?token=${token}`;
                          } catch (error) {
                            console.error('Error creating scanner session:', error);
                            toast.error('Failed to open scanner');
                          }
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100"
                      >
                        <ExternalLink className="w-4 h-4 text-gray-700" />
                        <div>
                          <p className="font-medium text-gray-900">Open scanner</p>
                          <p className="text-xs text-gray-500">Launch on this device</p>
                        </div>
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const response = await apiV2.post('/check-in/create-scanner-session');
                            const { token, expiresAt } = response.data;
                            const scannerUrl = `${window.location.origin}/qr-code-scanner?token=${token}`;
                            await navigator.clipboard.writeText(scannerUrl);
                            const expiry = new Date(expiresAt).toLocaleTimeString();
                            toast.success(`Link copied — expires at ${expiry}`);
                            setShowScannerDropdown(false);
                          } catch (error) {
                            console.error('Error creating scanner link:', error);
                            toast.error('Failed to create link');
                          }
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100"
                      >
                        <Clipboard className="w-4 h-4 text-gray-700" />
                        <div>
                          <p className="font-medium text-gray-900">Copy link</p>
                          <p className="text-xs text-gray-500">Share scanner URL with staff</p>
                        </div>
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const response = await apiV2.post('/check-in/create-scanner-session');
                            const { token } = response.data;
                            const scannerUrl = `${window.location.origin}/qr-code-scanner?token=${token}`;
                            setScannerQRUrl(scannerUrl);
                            setShowScannerQR(true);
                            setShowScannerDropdown(false);
                          } catch (error) {
                            console.error('Error creating scanner QR:', error);
                            toast.error('Failed to generate QR code');
                          }
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-3"
                      >
                        <QrCode className="w-4 h-4 text-gray-700" />
                        <div>
                          <p className="font-medium text-gray-900">Show QR code</p>
                          <p className="text-xs text-gray-500">Staff scan with phone camera</p>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Option 2: Scan with Phone — includes the staff PIN */}
              <div className="settings-card p-5 flex flex-col">
                <div className="flex items-center gap-2 mb-1">
                  <Camera className="w-4 h-4 text-gray-700" />
                  <span className="text-sm font-semibold text-gray-900">Scan with Phone</span>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Staff scans the ticket QR, then enters this PIN.
                </p>
                <div className="mt-auto">
                  <label className="block text-[11px] text-gray-500 mb-1.5">Staff check-in PIN</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={checkInPin}
                    onChange={(e) => setCheckInPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onBlur={() => { if (/^\d{6}$/.test(checkInPin) && checkInPin !== originalPin) handleSave(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && /^\d{6}$/.test(checkInPin)) e.target.blur(); }}
                    placeholder="000000"
                    maxLength={6}
                    className="font-mono text-base text-center h-9"
                    data-testid="checkin-pin-input"
                  />
                  <p className="text-[11px] text-gray-400 mt-2">Tokens last 12 hours — refresh daily.</p>
                </div>
              </div>
            </div>

            {/* Manual lookup search results render below the section via existing flow */}
          </div>
        )}

        {/* Recent Check-Ins */}
        {checkInEnabled && (
          <div className="card-soft p-5 sm:p-6">
            <div className="mb-4 flex items-baseline justify-between gap-3">
              <div>
                <h3 className="settings-section-title">Recent Check-Ins</h3>
                <p className="settings-section-subtitle">
                  {filteredCheckIns.length} {filteredCheckIns.length === 1 ? 'check-in' : 'check-ins'}
                  {(checkInQ || checkInStart || checkInEnd) ? ' match your filters' : ''}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleExportCheckIns}
                  disabled={exportingCheckIns || filteredCheckIns.length === 0}
                  className="h-8 px-3 text-xs font-medium text-white rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  data-testid="checkin-export-btn"
                >
                  {exportingCheckIns ? 'Exporting…' : 'Export CSV'}
                </button>
              </div>
            </div>

            {/* Search + Date filter */}
            <div className="space-y-2 mb-4">
              <Input
                placeholder="Search by name, code, service…"
                value={checkInQ}
                onChange={(e) => setCheckInQ(e.target.value)}
                className="h-9 text-sm"
                data-testid="checkin-search-input"
              />
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[140px]">
                  <label className="text-xs text-gray-500">From</label>
                  <Input
                    type="date"
                    value={checkInStart}
                    onChange={(e) => setCheckInStart(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="checkin-date-from"
                  />
                </div>
                <div className="flex-1 min-w-[140px]">
                  <label className="text-xs text-gray-500">To</label>
                  <Input
                    type="date"
                    value={checkInEnd}
                    onChange={(e) => setCheckInEnd(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="checkin-date-to"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">By</label>
                  <select
                    value={checkInDateField}
                    onChange={(e) => setCheckInDateField(e.target.value)}
                    className="h-9 text-sm border border-gray-200 rounded-md px-2 bg-white"
                    data-testid="checkin-date-field"
                  >
                    <option value="checkin">Check-in date</option>
                    <option value="booking">Booking date</option>
                  </select>
                </div>
                {(checkInQ || checkInStart || checkInEnd) && (
                  <button
                    type="button"
                    onClick={() => { setCheckInQ(''); setCheckInStart(''); setCheckInEnd(''); }}
                    className="h-9 px-3 text-xs text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
                    data-testid="checkin-clear-filters"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {checkInsError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">Failed to load check-ins. Please refresh.</p>
              </div>
            )}

            {filteredCheckIns.length === 0 && !checkInsError ? (
              <div className="text-center py-10 border border-dashed border-gray-200 rounded-lg">
                <QrCode className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-600">
                  {(checkInQ || checkInStart || checkInEnd) ? 'No check-ins match those filters' : 'No check-ins yet'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {(checkInQ || checkInStart || checkInEnd) ? 'Try widening your dates or clearing the search' : 'Scan your first customer to see them here'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {filteredCheckIns.slice(0, 50).map((booking) => (
                  <button
                    key={booking.id}
                    onClick={() => { setSelectedCheckIn(booking); setShowCheckInDetails(true); }}
                    className="w-full text-left flex items-start gap-3 py-3 px-2 hover:bg-gray-50 rounded-md transition"
                    data-testid={`recent-checkin-${booking.id}`}
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{booking.customerName}</p>
                      <p className="text-xs text-gray-500 truncate">{booking.serviceName} · <span className="font-mono">{booking.confirmationCode}</span></p>
                    </div>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {booking.checkInAt ? new Date(booking.checkInAt).toLocaleString() : '-'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Check-In Details Modal */}
      <Dialog open={showCheckInDetails} onOpenChange={setShowCheckInDetails}>
        <DialogContent className="max-w-lg sm:max-w-2xl lg:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Check-In Details</DialogTitle>
          </DialogHeader>
          
          {selectedCheckIn && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="settings-card p-4">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Customer</p>
                <p className="font-semibold text-lg text-gray-900">{selectedCheckIn.customerName}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                  {selectedCheckIn.customerEmail && (
                    <a href={`mailto:${selectedCheckIn.customerEmail}`} className="hover:text-blue-600 truncate" data-testid="modal-customer-email">
                      {selectedCheckIn.customerEmail}
                    </a>
                  )}
                  {selectedCheckIn.customerPhone && (
                    <a href={`tel:${selectedCheckIn.customerPhone}`} className="hover:text-blue-600" data-testid="modal-customer-phone">
                      {selectedCheckIn.customerPhone}
                    </a>
                  )}
                </div>
              </div>

              {/* Booking grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {selectedCheckIn.confirmationCode && (
                  <div className="settings-card p-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Booking ID</p>
                    <p className="font-mono font-semibold text-blue-600 truncate">{selectedCheckIn.confirmationCode}</p>
                  </div>
                )}
                {selectedCheckIn.partySize && (
                  <div className="settings-card p-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Party Size</p>
                    <p className="font-semibold text-gray-900">{selectedCheckIn.partySize} {selectedCheckIn.partySize === 1 ? 'guest' : 'guests'}</p>
                  </div>
                )}
                {selectedCheckIn.totalAmount != null && (
                  <div className="settings-card p-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Total</p>
                    <p className="font-semibold text-gray-900">${Number(selectedCheckIn.totalAmount).toFixed(2)}</p>
                  </div>
                )}
                {selectedCheckIn.preferredDate && (
                  <div className="settings-card p-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Date</p>
                    <p className="font-semibold text-gray-900">{selectedCheckIn.preferredDate}</p>
                  </div>
                )}
                {selectedCheckIn.preferredTime && (
                  <div className="settings-card p-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Time</p>
                    <p className="font-semibold text-gray-900">{selectedCheckIn.preferredTime}</p>
                  </div>
                )}
                {selectedCheckIn.serviceName && (
                  <div className="settings-card p-3 col-span-2 sm:col-span-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Service</p>
                    <p className="font-semibold text-gray-900">{selectedCheckIn.serviceName}</p>
                  </div>
                )}
              </div>

              {/* Status row */}
              {selectedCheckIn.checkInStatus === 'checked_in' ? (
                <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50/60 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-semibold text-emerald-900">Checked In</span>
                  </div>
                  {selectedCheckIn.checkInAt && (
                    <div className="text-right">
                      <p className="text-sm font-medium text-emerald-800">
                        {new Date(selectedCheckIn.checkInAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </p>
                      <p className="text-[11px] text-emerald-600 capitalize">via {selectedCheckIn.checkInMethod || 'manual'}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 border border-amber-200 bg-amber-50/60 rounded-lg p-3">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-900">Not checked in yet</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  onClick={async () => {
                    try {
                      const ticketUrl = await generateMagicLink(selectedCheckIn);
                      window.open(ticketUrl, '_blank', 'noopener,noreferrer');
                    } catch (error) {
                      toast.error('Failed to open ticket');
                    }
                  }}
                  className="flex-1 min-w-[140px] bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="modal-view-ticket-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Ticket
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      const ticketUrl = await generateMagicLink(selectedCheckIn);
                      await navigator.clipboard.writeText(ticketUrl);
                      toast.success('Magic link copied');
                    } catch (error) {
                      toast.error('Failed to generate magic link');
                    }
                  }}
                  variant="outline"
                  className="flex-1 min-w-[140px]"
                  data-testid="modal-copy-magic-link-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  onClick={() => setShowCheckInDetails(false)}
                  variant="outline"
                  className="flex-1 min-w-[100px]"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
        
      {/* Check-In Confirmation Modal */}
      <Dialog open={showCheckInConfirm} onOpenChange={setShowCheckInConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Check-In</DialogTitle>
          </DialogHeader>
          
          {bookingToCheckIn && (
            <div className="space-y-4">
              <p className="text-lg font-semibold text-gray-900">
                {bookingToCheckIn.customerName}
              </p>
              <p className="text-gray-600">
                {bookingToCheckIn.serviceName || 'Service'}
              </p>
              {(bookingToCheckIn.date || bookingToCheckIn.preferredDate) && (
                <p className="text-sm text-gray-600">
                  {bookingToCheckIn.date || bookingToCheckIn.preferredDate} at {bookingToCheckIn.startTime || bookingToCheckIn.preferredTime}
                </p>
              )}
              {bookingToCheckIn.groupSize && (
                <p className="text-sm font-medium text-gray-700">
                  Party size: {bookingToCheckIn.groupSize} guests
                </p>
              )}
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-medium text-amber-900 mb-2">Before checking in:</p>
                <p className="text-sm text-amber-800">Is the customer present for this booking?</p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => setShowCheckInConfirm(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmCheckIn}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Yes, Check In
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    
      {/* Search Results Modal - Table Design */}
      <Dialog open={showSearchResultsModal} onOpenChange={setShowSearchResultsModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0 rounded-xl">
          {/* Header */}
          <div className="px-5 pt-5 pb-4 border-b border-gray-100">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900">Booking Lookup</DialogTitle>
            </DialogHeader>
            
            {/* Search Bar */}
            <div className="mt-4 flex gap-2 items-center">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Name, email, phone, or booking ID..."
                  className="pl-10 h-10 sm:h-11 bg-gray-50 border-gray-200 focus:bg-white text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleManualCheckIn()}
                />
              </div>
              <Button
                onClick={handleManualCheckIn}
                disabled={checkingIn || !searchQuery.trim()}
                className="h-10 sm:h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {checkingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" />Search</>}
              </Button>
            </div>
            
            {/* Results count */}
            <p className="text-xs sm:text-sm text-gray-500 mt-3">
              Found <span className="font-semibold text-gray-700">{searchResults.length}</span> booking(s)
              {searchResults.length > searchResultsPerPage && (
                <span className="text-gray-400"> · Page {searchResultsPage} of {Math.ceil(searchResults.length / searchResultsPerPage)}</span>
              )}
            </p>
          </div>
          
          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Status</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Booking ID</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Customer</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Service</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Date & Time</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-600 whitespace-nowrap">Party</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Details</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Payment</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Ticket</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-600 whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {searchResults
                  .slice(
                    (searchResultsPage - 1) * searchResultsPerPage,
                    searchResultsPage * searchResultsPerPage
                  )
                  .map((booking) => {
                    // Calculate payment status
                    const totalAmount = booking.totalAmount || 0;
                    const amountPaid = booking.paidAmount || booking.amountPaid || 0;
                    const amountOwed = Math.max(0, totalAmount - amountPaid);
                    const isPaidInFull = (amountOwed === 0 && totalAmount > 0) || booking.paymentStatus === 'paid';
                    const isPartiallyPaid = amountPaid > 0 && amountOwed > 0;
                    const isUnpaid = amountPaid === 0 && totalAmount > 0;
                    
                    // Parse add-ons and custom question answers
                    const addOns = booking.selectedAddOns || booking.addOns || [];
                    const customAnswers = booking.customQuestionAnswers || booking.customAnswers || [];
                    const hasExtras = addOns.length > 0 || customAnswers.length > 0;
                    
                    // Format time with duration and timezone
                    const startTime = booking.startTime || booking.preferredTime || '-';
                    const endTime = booking.endTime || '';
                    const timezone = booking.timezone || '';
                    const duration = booking.duration;
                    
                    return (
                      <tr 
                        key={booking.id} 
                        className={`hover:bg-gray-50 transition-colors ${
                          booking.checkInStatus === 'checked_in' ? 'bg-green-50/50' : ''
                        }`}
                      >
                        {/* Status */}
                        <td className="px-3 py-3">
                          {booking.checkInStatus === 'checked_in' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                              <Check className="w-3 h-3" />
                              Checked In
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 whitespace-nowrap">
                              Not Checked In
                            </span>
                          )}
                        </td>
                        
                        {/* Booking ID */}
                        <td className="px-3 py-3">
                          <span className="font-mono text-xs font-semibold text-gray-900">{booking.confirmationCode || '-'}</span>
                        </td>
                        
                        {/* Customer */}
                        <td className="px-3 py-3">
                          <div>
                            <p className="font-semibold text-gray-900 whitespace-nowrap">{booking.customerName || '-'}</p>
                            <p className="text-xs text-gray-500">{booking.customerEmail || ''}</p>
                            {booking.customerPhone && <p className="text-xs text-gray-500">{booking.customerPhone}</p>}
                          </div>
                        </td>
                        
                        {/* Service */}
                        <td className="px-3 py-3">
                          <span className="text-sm max-w-[120px] truncate block" title={booking.serviceName}>{booking.serviceName || '-'}</span>
                        </td>
                        
                        {/* Date & Time (combined with duration & timezone) */}
                        <td className="px-3 py-3">
                          <div className="text-xs">
                            <p className="font-medium text-gray-900 whitespace-nowrap">
                              {(booking.date || booking.preferredDate) 
                                ? new Date(booking.date || booking.preferredDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : '-'}
                            </p>
                            <p className="text-gray-700 whitespace-nowrap">
                              {startTime}{endTime ? ` - ${endTime}` : ''}{duration ? ` (${duration}min)` : ''}
                            </p>
                            {timezone && <p className="text-gray-400 text-[10px]">{timezone}</p>}
                          </div>
                        </td>
                        
                        {/* Party Size */}
                        <td className="px-3 py-3 text-center">
                          <span className="text-sm font-medium text-gray-700">{booking.groupSize || booking.partySize || '-'}</span>
                        </td>
                        
                        {/* Details: Add-ons + Custom Answers */}
                        <td className="px-3 py-3">
                          <div className="text-xs space-y-1 max-w-[200px]">
                            {/* Add-ons */}
                            {addOns.length > 0 && (
                              <div>
                                <p className="text-gray-500 font-medium">Add-ons:</p>
                                {addOns.slice(0, 2).map((addon, idx) => (
                                  <p key={idx} className="text-gray-700 truncate">
                                    • {addon.title || addon.name || 'Add-on'}{addon.quantity > 1 ? ` (x${addon.quantity})` : ''}{addon.price ? ` - $${addon.price}` : ''}
                                  </p>
                                ))}
                                {addOns.length > 2 && <p className="text-gray-400">+{addOns.length - 2} more</p>}
                              </div>
                            )}
                            
                            {/* Custom Question Answers */}
                            {customAnswers.length > 0 && (
                              <div>
                                <p className="text-gray-500 font-medium">Custom Info:</p>
                                {customAnswers.slice(0, 2).map((qa, idx) => (
                                  <p key={idx} className="text-gray-700 truncate" title={`${qa.question}: ${qa.answer}`}>
                                    • {qa.answer || qa.value || '-'}
                                  </p>
                                ))}
                                {customAnswers.length > 2 && <p className="text-gray-400">+{customAnswers.length - 2} more</p>}
                              </div>
                            )}
                            
                            {/* Check-in time if checked in */}
                            {booking.checkInStatus === 'checked_in' && booking.checkInAt && (
                              <p className="text-green-600">
                                ✓ {new Date(booking.checkInAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                            
                            {!hasExtras && !booking.checkInAt && <span className="text-gray-400">-</span>}
                          </div>
                        </td>
                        
                        {/* Payment */}
                        <td className="px-3 py-3">
                          <div className="space-y-1">
                            {isPaidInFull ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                                <Check className="w-3 h-3" />
                                Paid
                              </span>
                            ) : isPartiallyPaid ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                                <AlertTriangle className="w-3 h-3" />
                                Partial
                              </span>
                            ) : isUnpaid ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-700 whitespace-nowrap">
                                Unpaid
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                {booking.paymentStatus || 'N/A'}
                              </span>
                            )}
                            <p className="text-xs font-semibold text-gray-900">
                              {totalAmount > 0 ? `$${totalAmount.toFixed(2)}` : '-'}
                            </p>
                            {booking.addOnsTotal > 0 && (
                              <p className="text-[10px] text-gray-500">+${booking.addOnsTotal.toFixed(2)} add-ons</p>
                            )}
                          </div>
                        </td>
                        
                        {/* Ticket Actions */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={async () => {
                                try {
                                  const ticketUrl = await generateMagicLink(booking);
                                  window.open(ticketUrl, '_blank', 'noopener,noreferrer');
                                } catch (error) {
                                  toast.error('Failed to open ticket');
                                }
                              }}
                              className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600 transition-colors"
                              title="View ticket in new tab"
                              data-testid={`view-ticket-${booking.id}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const ticketUrl = await generateMagicLink(booking);
                                  await navigator.clipboard.writeText(ticketUrl);
                                  toast.success('Magic link copied');
                                } catch (error) {
                                  toast.error('Failed to generate magic link');
                                }
                              }}
                              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                              title="Copy magic link"
                            >
                              <Clipboard className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openEmailModal(booking)}
                              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                              title="Email magic link"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        
                        {/* Action */}
                        <td className="px-3 py-3 text-center">
                          {booking.checkInStatus !== 'checked_in' ? (
                            <Button
                              onClick={() => checkInBooking(booking.id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white h-8 px-3 whitespace-nowrap"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Check In
                            </Button>
                          ) : (
                            <span className="text-green-600 font-medium text-sm whitespace-nowrap">✓ Done</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            
            {/* Empty State */}
            {searchResults.length === 0 && (
              <div className="text-center py-12">
                <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No bookings found</p>
                <p className="text-gray-400 text-xs mt-1">Try a different search term</p>
              </div>
            )}
          </div>
          
          {/* Footer with pagination */}
          <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
            {/* Pagination */}
            {searchResults.length > searchResultsPerPage && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <Button
                  onClick={() => setSearchResultsPage(p => Math.max(1, p - 1))}
                  disabled={searchResultsPage === 1}
                  variant="outline"
                  size="sm"
                  className="h-8"
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.ceil(searchResults.length / searchResultsPerPage) }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setSearchResultsPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition ${
                        searchResultsPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>
                
                <Button
                  onClick={() => setSearchResultsPage(p => Math.min(Math.ceil(searchResults.length / searchResultsPerPage), p + 1))}
                  disabled={searchResultsPage === Math.ceil(searchResults.length / searchResultsPerPage)}
                  variant="outline"
                  size="sm"
                  className="h-8"
                >
                  Next
                </Button>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={() => setShowSearchResultsModal(false)}
                variant="outline"
                className="flex-1 h-10"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setSearchResults([]);
                  setSearchQuery('');
                  setSearchResultsPage(1);
                  setShowSearchResultsModal(false);
                }}
                className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Clear & Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Ticket via Email</DialogTitle>
          </DialogHeader>
          
          {emailBooking && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  {emailBooking.customerName || 'Customer'}
                </p>
                <p className="text-xs text-blue-700">
                  Booking: {emailBooking.confirmationCode}
                </p>
              </div>
              
              <div>
                <Label className="block text-sm font-semibold text-gray-700 mb-2">
                  Customer Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="Enter customer email..."
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {emailBooking.customerEmail ? 'Pre-filled from booking' : 'No email on file - please ask customer for their email'}
                </p>
              </div>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">Email Preview:</p>
                <p className="text-xs text-gray-600 whitespace-pre-line">
                  Hi {emailBooking.customerName || 'there'}!
                  {'\n\n'}
                  Here is your booking ticket:
                  {'\n'}
                  [Ticket Link]
                  {'\n\n'}
                  See you soon!
                  {'\n\n'}
                  {localStorage.getItem('organizationName') || 'BookingFlows'}
                </p>
                <p className="text-xs text-gray-500 mt-2 italic">
                  Fixed template - ensures professional communication
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800">
                  <strong>Rate Limit:</strong> Maximum 10 emails per customer per day
                </p>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailTo('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={sendingEmail}
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendTicketEmail}
                  disabled={sendingEmail || !emailTo.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {sendingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Scanner QR Code Modal */}
      <Dialog open={showScannerQR} onOpenChange={setShowScannerQR}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                <QrCode className="w-5 h-5 text-white" />
              </div>
              Scan to Open Scanner
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Staff can scan this QR code with their phone camera to instantly open the scanner interface
            </p>
            
            {/* QR Code */}
            <div className="bg-white border-4 border-gray-100 rounded-xl p-6 flex items-center justify-center">
              {scannerQRUrl && (
                <QRCodeCanvas
                  value={scannerQRUrl}
                  size={280}
                  level="H"
                  includeMargin={true}
                />
              )}
            </div>
            
            {/* Info */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-xs text-purple-900 font-medium mb-1">⏱️ Session Info</p>
              <p className="text-xs text-purple-800">
                Scanner session valid for 12 hours. Staff can scan this QR code to check in customers on their phone.
              </p>
            </div>
            
            {/* URL Preview */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500">Scanner URL:</p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(scannerQRUrl);
                    toast.success('URL copied!');
                  }}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  <Clipboard className="w-3.5 h-3.5" />
                  Copy
                </button>
              </div>
              <div className="bg-gray-100 rounded-lg p-2 font-mono text-xs text-gray-700 break-all">
                {scannerQRUrl}
              </div>
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowScannerQR(false)}
                variant="outline"
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default CheckInTab;
